import React, { useState } from "react";
import { useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AsyncPaginate } from "react-select-async-paginate";
import Swal from "sweetalert2";
import PhoneInput from "components/common/PhoneInput";
import { selectCustomStyles } from "components/common/Select";
import ErrorMessageComponent from "components/common/ErrorMessage";
import { usePermissions } from "layouts/Admin";
import { fetchLanguages } from "slices/languageSlice";

const initialFormState = {
  agency_interpreter_id: null,
  code: "",
  name: "",
  email: "",
  phone: "",
  language_ids: [],
};

const AgencyInterpreters = ({ control, setValue, getValues, errors, error, ErrorMessage }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { checkPermission } = usePermissions();
  const canEdit = checkPermission("Edit Interpreter");
  const ErrorMsg = ErrorMessage || ErrorMessageComponent;

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "agency_interpreters",
  });

  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});

  const loadLanguageOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchLanguages(page, {
      pageLength: 20,
      sortBy: "id",
      sortDirection: "asc",
      search: inputValue,
    }, true, true));

    return {
      options: result?.data?.map((language) => ({ value: language.id, label: language.name })) || [],
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };

  const handleOpenAddForm = () => {
    setEditingIndex(null);
    setFormData(initialFormState);
    setFormErrors({});
    setShowForm(true);
  };

  const handleEdit = (index, row) => {
    setEditingIndex(index);
    setFormData({
      agency_interpreter_id: row.agency_interpreter_id || null,
      code: row.code || "",
      name: row.name || "",
      email: row.email || "",
      phone: row.phone || "",
      language_ids: Array.isArray(row.language_ids) ? row.language_ids : [],
    });
    setFormErrors({});
    setShowForm(true);
  };

  const handleDelete = async (index) => {
    const result = await Swal.fire({
      title: t("common.areYouSure", "Are you sure?"),
      text: t("interpreter.deleteAgencyInterpreterText", "This agency interpreter will be removed from the list."),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("common.delete", "Delete"),
      cancelButtonText: t("common.cancel", "Cancel"),
    });

    if (!result.isConfirmed) return;

    if (editingIndex === index) {
      handleCancel();
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
    remove(index);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingIndex(null);
    setFormData(initialFormState);
    setFormErrors({});
  };

  const handleSave = () => {
    const newErrors = {};
    if (formData.code?.trim()) {
      const duplicateCode = fields.some(
        (f, idx) => idx !== editingIndex && f.code && f.code.toLowerCase().trim() === formData.code.toLowerCase().trim()
      );
      if (duplicateCode) {
        newErrors.code = t("interpreter.codeDuplicate", "Interpreter ID already added in list");
      }
    }
    if (!formData.name?.trim()) {
      newErrors.name = t("interpreter.nameRequired", "Name is required");
    }
    if (!formData.email?.trim()) {
      newErrors.email = t("interpreter.emailRequired", "Email is required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = t("interpreter.emailInvalid", "Please enter a valid email address");
    } else {
      const duplicate = fields.some(
        (f, idx) => idx !== editingIndex && f.email?.toLowerCase().trim() === formData.email.toLowerCase().trim()
      );
      if (duplicate) {
        newErrors.email = t("interpreter.emailDuplicate", "Email already added in list");
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    setFormErrors({});

    const payload = {
      agency_interpreter_id: formData.agency_interpreter_id || null,
      code: formData.code?.trim() || "",
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone || "",
      language_ids: formData.language_ids || [],
    };

    if (editingIndex !== null) {
      update(editingIndex, payload);
      if (setValue) {
        setValue(`agency_interpreters.${editingIndex}`, payload, { shouldDirty: true, shouldValidate: true });
      }
    } else {
      append(payload);
    }

    handleCancel();
  };

  return (
    <section className="row-primary w-100" style={{ display: "block", marginTop: 0 }}>
      <h2 className="fst-italic fw-bold mt-0">{t("interpreter.agencyInterpreters", "Agency Interpreters")}</h2>

      <div style={{ overflowX: "auto", marginBottom: 15 }}>
        <table>
          <thead>
            <tr>
              <th>{t("selectInterpreter.agencyInterpreterCode", "Interpreter ID")}</th>
              <th>{t("interpreter.name", "Name")}</th>
              <th>{t("interpreter.email", "Email")}</th>
              <th>{t("interpreter.phoneNumber", "Phone Number")}</th>
              <th>{t("interpreter.languages", "Languages")}</th>
              {canEdit && <th>{t("interpreter.actions", "Actions")}</th>}
            </tr>
          </thead>
          <tbody>
            {fields.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? "6" : "5"}>
                  {t("interpreter.noAgencyInterpreters", "No agency interpreters added")}
                </td>
              </tr>
            ) : (
              fields.map((row, index) => (
                <tr key={row.id}>
                  <td>{row.code || t("common.nA", "N/A")}</td>
                  <td>{row.name}</td>
                  <td>{row.email || t("common.nA", "N/A")}</td>
                  <td>{row.phone || t("common.nA", "N/A")}</td>
                  <td>
                    {Array.isArray(row.language_ids) && row.language_ids.length > 0
                      ? row.language_ids.map((l) => l.label || l.name).filter(Boolean).join(", ")
                      : t("common.nA", "N/A")}
                  </td>
                  {canEdit && (
                    <td>
                      <button
                        type="button"
                        className="table_edit"
                        onClick={() => handleEdit(index, row)}
                      >
                        {t("interpreter.edit", "Edit")}
                      </button>{" "}
                      <button
                        type="button"
                        className="table_delete"
                        onClick={() => handleDelete(index)}
                      >
                        {t("common.delete", "Delete")}
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {error?.errors && Object.keys(error.errors).some((k) => k.startsWith("agency_interpreters")) && (
        <div className="mt-2 mb-2">
          {Object.entries(error.errors)
            .filter(([k]) => k.startsWith("agency_interpreters"))
            .map(([k, msgs]) => (
              <ErrorMsg key={k} text={Array.isArray(msgs) ? msgs[0] : msgs} />
            ))}
        </div>
      )}

      {canEdit && (
        <>
          <button
            type="button"
            className="main_button"
            onClick={handleOpenAddForm}
          >
            {t("interpreter.addInterpreter", "Add Interpreter")}
          </button>

          {showForm && (
            <div className="profile_form" style={{ marginTop: 20 }}>
              <h3 className="fst-italic fw-bold mt-0">
                {editingIndex !== null
                  ? t("interpreter.editAgencyInterpreter", "Edit Agency Interpreter")
                  : t("interpreter.addAgencyInterpreter", "Add Agency Interpreter")}
              </h3>

              <div className="row-primary">
                <div className="row-primary w-100">
                  <h2>{t("selectInterpreter.agencyInterpreterCode", "Interpreter ID")}</h2>
                  <div className="w-100">
                    <input
                      className="input-in type_inp"
                      type="text"
                      placeholder={t("selectInterpreter.agencyInterpreterCodePlaceholder", "Enter interpreter ID")}
                      value={formData.code || ""}
                      onChange={(e) => {
                        setFormData({ ...formData, code: e.target.value });
                        if (formErrors.code) setFormErrors({ ...formErrors, code: null });
                      }}
                    />
                    {formErrors.code && <ErrorMsg text={formErrors.code} />}
                  </div>
                </div>

                <div className="row-primary w-100">
                  <h2>{t("interpreter.name", "Name")} *</h2>
                  <div className="w-100">
                    <input
                      className="input-in type_inp"
                      type="text"
                      placeholder={t("interpreter.namePlaceholder", "Enter name")}
                      value={formData.name || ""}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        if (formErrors.name) setFormErrors({ ...formErrors, name: null });
                      }}
                    />
                    {formErrors.name && <ErrorMsg text={formErrors.name} />}
                  </div>
                </div>
              </div>

              <div className="row-primary">
                <div className="row-primary w-100">
                  <h2>{t("interpreter.email", "Email")} *</h2>
                  <div className="w-100">
                    <input
                      className="input-in type_inp"
                      type="email"
                      placeholder={t("interpreter.emailPlaceholder", "Enter email")}
                      value={formData.email || ""}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        if (formErrors.email) setFormErrors({ ...formErrors, email: null });
                      }}
                    />
                    {formErrors.email && <ErrorMsg text={formErrors.email} />}
                  </div>
                </div>

                <div className="row-primary w-100">
                  <h2>{t("interpreter.phoneNumber", "Phone Number")}</h2>
                  <div className="w-100">
                    <PhoneInput
                      className="input-in type_inp"
                      value={formData.phone || ""}
                      onChange={(val) => setFormData({ ...formData, phone: val })}
                    />
                  </div>
                </div>
              </div>

              <div className="row-primary">
                <div className="row-primary w-100">
                  <h2>{t("interpreter.languages", "Languages")}</h2>
                  <div className="w-100">
                    <AsyncPaginate
                      isMulti
                      isClearable
                      menuPlacement="auto"
                      styles={selectCustomStyles}
                      value={formData.language_ids || []}
                      loadOptions={loadLanguageOptions}
                      onChange={(selected) => setFormData({ ...formData, language_ids: selected || [] })}
                      additional={{ page: 1 }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 20 }}>
                <button
                  type="button"
                  className="main_button"
                  onClick={handleSave}
                >
                  {editingIndex !== null
                    ? t("common.update", "Update")
                    : t("common.add", "Add")}
                </button>{" "}
                <button
                  type="button"
                  className="Cancle_button"
                  onClick={handleCancel}
                >
                  {t("common.cancel", "Cancel")}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default AgencyInterpreters;
